import { Link } from '@tanstack/react-router';
import { ArrowRight, ListChecks } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { PostureChip, type PostureStatus } from '@/components/ui/posture-chip';
import { Spinner } from '@/components/ui/spinner';
import { useContractAttentionQuery, type AttentionItem } from '@/lib/api/contract-attention';

/**
 * "What needs attention" — the guidance panel for Contract Compliance.
 *
 * Each tab shows its own slice of the contract. This panel answers the
 * cross-cutting question first: what should the IV&V lead act on today?
 * Items are rule-derived from live contract state (no AI), ordered by
 * severity, each with the specifics and a link to where it gets fixed.
 * Live refresh keeps it current: resolving the cause removes the item.
 *
 * Severity uses the posture chip primitive (critical / at-risk /
 * unassessed) rather than inventing a new colour scale.
 */

const SEVERITY_POSTURE: Record<
  AttentionItem['severity'],
  { status: PostureStatus; label: string }
> = {
  critical: { status: 'critical', label: 'Act now' },
  high: { status: 'at-risk', label: 'High' },
  medium: { status: 'at-risk', label: 'Medium' },
  info: { status: 'unassessed', label: 'For awareness' },
};

const TARGET_ROUTE = {
  deliverables: '/engagements/$engagementId/contract/deliverables',
  deviations: '/engagements/$engagementId/contract/deviations',
  changes: '/engagements/$engagementId/contract/changes',
  checkpoints: '/engagements/$engagementId/contract/checkpoints',
  pulse: '/engagements/$engagementId/contract/pulse',
  proposals: '/engagements/$engagementId/proposals',
} as const;

export function ContractAttentionPanel({
  engagementId,
  /** Hide the item that points at the tab the panel is already on. */
  hideTarget,
  /** Show only the most urgent N (the Overview); the rest are one click away. */
  limit,
}: {
  engagementId: string;
  hideTarget?: AttentionItem['target'];
  limit?: number;
}) {
  const attention = useContractAttentionQuery(engagementId);
  const all = (attention.data?.items ?? []).filter((i) => i.target !== hideTarget);
  const items = limit !== undefined ? all.slice(0, limit) : all;
  const hidden = all.length - items.length;

  return (
    <Card aria-labelledby="contract-attention-title">
      <CardContent className="p-0">
        <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-ink-100 px-5 py-3">
          <div>
            <h2
              id="contract-attention-title"
              className="flex items-center gap-2 text-h3 font-semibold text-ink-900"
            >
              <ListChecks className="h-4 w-4" aria-hidden="true" />
              What needs attention
            </h2>
            <p className="text-meta text-ink-500">
              Worked out from the contract records by fixed rules, most urgent first. It updates as
              documents are processed and decisions are made.
            </p>
          </div>
          {attention.data ? (
            <span className="text-meta text-ink-500">
              {all.length} {all.length === 1 ? 'item' : 'items'}
            </span>
          ) : null}
        </div>
        <div className="px-5 py-3">
          {attention.isLoading ? (
            <div className="flex items-center gap-2 text-body-sm text-ink-500">
              <Spinner /> Checking the contract…
            </div>
          ) : attention.error ? (
            <p className="text-body-sm text-ink-500">
              The attention list could not be loaded. The tabs below still show current data.
            </p>
          ) : items.length === 0 ? (
            <p className="text-body-sm text-ink-700">
              Nothing needs attention right now. No overdue deliverables, open escalations,
              unregistered change orders or pending reviews were found.
            </p>
          ) : (
            <ul className="divide-y divide-ink-100" role="list">
              {items.map((item) => {
                const sev = SEVERITY_POSTURE[item.severity];
                return (
                  <li
                    key={item.kind}
                    className="flex flex-wrap items-start justify-between gap-3 py-2.5"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <PostureChip status={sev.status}>{sev.label}</PostureChip>
                        <span className="text-body-sm font-semibold text-ink-900">
                          {item.title}
                        </span>
                      </div>
                      <p className="mt-0.5 text-meta leading-[18px] text-ink-700">{item.detail}</p>
                    </div>
                    <Link
                      to={TARGET_ROUTE[item.target]}
                      params={{ engagementId }}
                      className="inline-flex flex-none items-center gap-1 rounded-card border border-ink-200 px-2.5 py-1.5 text-meta font-medium text-ink-900 no-underline hover:bg-paper-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600"
                    >
                      {item.action_label}
                      <ArrowRight className="h-3 w-3" aria-hidden="true" />
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
          {hidden > 0 ? (
            <Link
              to="/engagements/$engagementId/contract/baseline"
              params={{ engagementId }}
              className="mt-1 inline-flex items-center gap-1 text-body-sm font-medium text-accent-amber-700 underline-offset-2 hover:underline"
            >
              {hidden} more on Contract Compliance
              <ArrowRight className="h-3 w-3" aria-hidden="true" />
            </Link>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}
